import { connect } from "react-redux";

import { AbsoluteResolvedModelPath } from "../../../../../Common/ModelPath/ModelPath";
import { setToModel } from "../../../Engine/ActionCreators/AllActionCreators";
import { queryPicklistAction } from "../../../Engine/ActionCreators/PicklistActionCreators";
import { CandyDispatch } from "../../../Engine/Core/Store/CandyReduxTypes";
import { FullFormState } from "../../../Engine/Core/Store/FullFormState";
import { ViewModelSelect } from "../../../Helpers/StateAccessors/ViewModelSelect";
import { withControlDebugInfo } from "../../withControlDebugInfo";
import { EvaluableProp, withEvaluators } from "../../withEvaluators";
import { withMetrics } from "../../withMetrics";
import { withResolvePaths } from "../../withResolvePaths";
import { withViewModelSelect } from "../../withViewModelSelect";
import { withExternalMountingEventReporting } from "../../Services/ExternalMountingEventReporting/withExternalMountingEventReporting";
import { withFocusManagement } from "../../Services/FocusManagement/withFocusManagement";
import { PicklistDataItem } from "../../../Engine/Core/Picklists/PicklistTypes";
import { withFocusOnNextValueEditor } from "../../../Helpers/JumpByEnterOnNextControl/WithFocusOnNextValueEditor";
import { checkIsSuitableToClick, externalOnClickHandler } from "../../../Engine/ActionCreators/ExternalOnClickAction";
import { WithHighlightedInjectableProps, withHighlighting } from "../../withHighlighting";
import { isNotNullOrUndefined } from "../../../../../Common/TypingUtils";
import {
    FormActionHandlerExecutionInjectableProps,
    withFormActionHandlerExecution,
} from "../../Internals/FormActionHandlers/WithFormActionHandlerExecution";
import { FormActionHandler } from "../../Internals/FormActionHandlers/FormActionHandler";

import { Select } from "./Select";

interface SelectOwnProps extends WithHighlightedInjectableProps, FormActionHandlerExecutionInjectableProps {
    bindingPath: AbsoluteResolvedModelPath;
    viewModelSelect: ViewModelSelect;
    gPaths?: string[];
    disabled?: boolean;
    disabled2?: EvaluableProp<boolean>;
    processEvaluableProp: <T>(state: FullFormState, prop: EvaluableProp<T>) => T;
    callHelperOnChange?: FormActionHandler;
}

const mapStateToProps = (state: FullFormState, ownProps: SelectOwnProps) => {
    const { bindingPath, viewModelSelect, processEvaluableProp, disabled2, disabled } = ownProps;
    const controlModel = viewModelSelect.controlModel(state, bindingPath);

    return {
        value: controlModel.value ?? "",
        error: controlModel.error,
        warning: controlModel.warning,
        disabled: processEvaluableProp(state, disabled2) || (controlModel.disabled ?? disabled),
        isTooltipEnabled: controlModel.isTooltipEnabled,
        highlighted: ownProps.highlighted,
        isReadOnly: state.settings.isReadOnly,
        isLink: checkIsSuitableToClick(state.settings, bindingPath),
    };
};

const mapDispatchToProps = (dispatch: CandyDispatch, ownProps: SelectOwnProps) => {
    const { bindingPath } = ownProps;

    return {
        onChange: async (value: string) => {
            dispatch(setToModel(bindingPath, "value", value));
            if (isNotNullOrUndefined(ownProps.callHelperOnChange)) {
                await ownProps.executeFormActionHandler(ownProps.callHelperOnChange, value);
            }
        },
        setPicklistDataItem: (value: undefined | PicklistDataItem): void =>
            dispatch(setToModel(bindingPath, "picklist", value)),
        onQueryPicklist: () => dispatch(queryPicklistAction(ownProps)),
        onClick: () => externalOnClickHandler(dispatch, bindingPath),
        onQueryOnlyGPathData: () =>
            dispatch(
                queryPicklistAction({
                    gId: undefined,
                    gPaths: ownProps.gPaths,
                })
            ),
    };
};

export const SelectContainer = withMetrics(
    withFormActionHandlerExecution(
        withFocusOnNextValueEditor(
            withResolvePaths(
                withViewModelSelect(
                    withEvaluators(
                        withHighlighting(
                            connect(
                                mapStateToProps,
                                mapDispatchToProps
                            )(withFocusManagement(withExternalMountingEventReporting(withControlDebugInfo(Select))))
                        )
                    )
                )
            )
        )
    )
);
